import { Controller } from "@hotwired/stimulus"

// Attaches a reCAPTCHA Enterprise (v3) token to the form before it submits.
//
// The score request is started as soon as the form connects, not on submit:
// in the native app's web view it takes ~7s (Safari ~1.5s), far longer than
// anyone should wait on the button. By the time the form is filled in the
// token is ready. Tokens are good for two minutes, so a stale one is
// re-requested on submit. If it still isn't ready within a second of the
// tap, the form goes without it: the server then falls back to the v2
// checkbox rather than leaving the user staring at a stuck button.
const TOKEN_MAX_AGE_MS = 90 * 1000
const SUBMIT_WAIT_MS = 1000

export default class extends Controller {
  connect() {
    this.recaptchaProcessed = false
    this.tokenPromise = null
    this.tokenRequestedAt = 0
    this.requestToken()
  }

  requestToken() {
    const siteKey = this.siteKey
    if (!siteKey || !window.grecaptcha?.enterprise) return null

    this.tokenRequestedAt = Date.now()
    this.tokenPromise = new Promise((resolve, reject) => {
      grecaptcha.enterprise.ready(async () => {
        try {
          resolve(await grecaptcha.enterprise.execute(siteKey, { action: 'signup' }))
        } catch (err) {
          reject(err)
        }
      })
    })
    // A failed warm-up is retried on submit; don't surface it as unhandled.
    this.tokenPromise.catch(() => {})
    return this.tokenPromise
  }

  get siteKey() {
    return document.querySelector('meta[name="recaptcha-site-key"]')?.content
  }

  async handleSubmit(e) {
    if (this.recaptchaProcessed) {
      return
    }

    const hiddenInput = document.getElementById('recaptcha_enterprise_token')
    const submitBtn = this.element.querySelector('input[type="submit"], button[type="submit"]')
    const originalText = submitBtn?.value || submitBtn?.textContent
    
    if (this.siteKey && window.grecaptcha?.enterprise && hiddenInput) {
      e.preventDefault()
      
      // Show loading state
      if (submitBtn) {
        submitBtn.disabled = true
        if (submitBtn.tagName === 'INPUT') {
          submitBtn.value = 'Loading...'
        } else {
          submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Loading...'
        }
      }
      
      try {
        let tokenPromise = this.tokenPromise
        if (!tokenPromise || Date.now() - this.tokenRequestedAt > TOKEN_MAX_AGE_MS) {
          tokenPromise = this.requestToken()
        }

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), SUBMIT_WAIT_MS)
        )
        
        const token = await Promise.race([tokenPromise, timeoutPromise])
        
        if (token) {
          hiddenInput.value = token
        }
      } catch (err) {
        console.error('[RECAPTCHA] Error:', err.message)
      }
      
      // Reset button before submitting
      if (submitBtn) {
        submitBtn.disabled = false
        if (submitBtn.tagName === 'INPUT') {
          submitBtn.value = originalText
        } else {
          submitBtn.textContent = originalText
        }
      }
      
      this.recaptchaProcessed = true
      this.element.requestSubmit()
    }
  }
}
